import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";
import { useMediaQuery, useTheme } from "@mui/material";

// Shared components
import DataTable from "components/DataTable";
import PageHeader from "components/PageHeader";

// Custom hooks
import useHarvestData from "layouts/tables/data/harvestReportTableData";

function Harvest() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [paymentType, setPaymentType] = React.useState("Harvest");

  const {
    columns: allColumns,
    rows,
    isLoading,
    error,
    fetchHarvestData,
    handleExport,
  } = useHarvestData();

  // Define which columns to show/hide based on screen size
  const columns = React.useMemo(() => {
    if (isMobile) {
      return allColumns.filter(
        (col) =>
          col.accessor === "serial" || col.accessor === "harvestName" || col.accessor === "phone"
      );
    }
    return allColumns;
  }, [allColumns, isMobile]);

  const [dateRange, setDateRange] = React.useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  const [dateRangeType, setDateRangeType] = React.useState("daily");

  const handleStartDateChange = (date) => {
    const newDateRange = {
      startDate: startOfDay(date),
      endDate: dateRange.endDate,
    };
    setDateRange(newDateRange);
    fetchHarvestData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: endOfDay(date),
    };
    setDateRange(newDateRange);
    fetchHarvestData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const newDateRange = {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
    setDateRange(newDateRange);
    setDateRangeType("daily");
    fetchHarvestData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);

    const today = new Date();
    let startDate, endDate;

    switch (type) {
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate = endOfYear(today);
        break;
      case "custom":
        // Keep current dates for custom range
        return;
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
    }

    const newDateRange = { startDate, endDate };
    setDateRange(newDateRange);
    fetchHarvestData(startDate, endDate, paymentType);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading Harvest...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box pt={6} pb={3}>
      <PageHeader
        title="Harvest Report"
        subtitle={
          <>
            {formatDisplayDate(dateRange.startDate)}
            {!isSameDay(dateRange.startDate, dateRange.endDate) &&
              ` to ${formatDisplayDate(dateRange.endDate)}`}
          </>
        }
        actions={
          <>
            <TextField
              select
              size="small"
              value={dateRangeType}
              onChange={handleDateRangeTypeChange}
              sx={{ width: 120 }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </TextField>

            {dateRangeType === "custom" && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  maxDate={dateRange.endDate}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 150 }} />
                  )}
                />
                <DatePicker
                  label="To Date"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  minDate={dateRange.startDate}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 150 }} />
                  )}
                />
              </LocalizationProvider>
            )}

            <TextField
              select
              size="small"
              value={paymentType}
              onChange={handleDateRangeTypeChange}
              sx={{ width: 120 }}
            >
              <MenuItem value="welfare">Welfare</MenuItem>
              <MenuItem value="tithe">Tithe</MenuItem>
              <MenuItem value="offering">Offering</MenuItem>
              <MenuItem value="donation">Donation</MenuItem>
              <MenuItem value="harvest">Harvest</MenuItem>
            </TextField>

            <Button variant="outlined" onClick={handleTodayClick} sx={{ minWidth: "100px" }}>
              Today
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("csv")}
              startIcon={<Icon>download</Icon>}
            >
              CSV
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("pdf")}
              startIcon={<Icon>picture_as_pdf</Icon>}
            >
              PDF
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("print")}
              startIcon={<Icon>print</Icon>}
            >
              Print
            </Button>
          </>
        }
      />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box>
              <DataTable
                table={{
                  columns,
                  rows: rows.map((row) => ({
                    ...row,
                    // Ensure all data is included even if columns are hidden
                    originalData: row.originalData || {
                      id: row.id,
                      memberName: row.memberName,
                      type: row.type,
                      amount: row.amount,
                      method: row.method,
                      paymentDate: row.paymentDate,
                    },
                  })),
                }}
                isSorted={false}
                entriesPerPage={true}
                showTotalEntries={true}
                noEndBorder
                canSearch={true}
                searchLabel="Search harvest..."
                printFullTable // Ensure all columns print regardless of screen size
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Harvest;
